/*****************************************************************
 *  CUSTOMER BUTLER - THE LOOK LOADER AND THE LOOK SWITCH          *
 *  File: apps/web/src/pages/customer/customerLook.js              *
 *  What it does:                                                  *
 *   1. useButlerLook() links the suite's ONE butler-tokens.css    *
 *      (served by Customer Butler's design door) into the page,   *
 *      once, so the customer pages wear the HR Butler look.       *
 *   2. useCustomerSkin() remembers, per person on this device,    *
 *      which look they chose: HR (default), clean or classic.     *
 *   3. shouldHideSuiteShell() tells App.js when to leave the      *
 *      suite bars off (Customer Butler pages in the HR look).     *
 *  Reads: localStorage "cb-skin-v2" / "cb-skin" (per viewer).     *
 *  Writes: one <link> tag in <head>; that localStorage key.       *
 *****************************************************************/

/* WHY (2026-10-14, James): the founding rule - "skins are only ever added,
   never removed". The clean Zoho-arranged pages are the new default; the
   classic customer pages stay one tap away. */

import { useCallback, useEffect, useState } from 'react';
import { API } from '../../lib/api';

/*****************************************************************
 *  SECTION 1 - LINKING THE DESIGN STANDARD                        *
 *****************************************************************/

const TOKENS_LINK_ID = 'cb-butler-tokens';                      // so the link is added once
const TOKENS_ADDRESS = `${API}/customer/design/butler-tokens.css`; // Customer Butler's design door

/* ---- addTokensLinkOnce() ---------------------------------------
   Puts the stylesheet link in <head> if it is not there yet.
   In: nothing.  Out: nothing. Never adds a second copy.         */
function addTokensLinkOnce() {
  const alreadyLinked = document.getElementById(TOKENS_LINK_ID);

  if (alreadyLinked) {
    return;
  }

  const tokensLink = document.createElement('link');

  tokensLink.id = TOKENS_LINK_ID;
  tokensLink.rel = 'stylesheet';
  tokensLink.href = TOKENS_ADDRESS;

  document.head.appendChild(tokensLink);
}

/* ---- useButlerLook() -- call at the top of every clean page ---- */
export function useButlerLook() {
  useEffect(() => {
    addTokensLinkOnce();
  }, []);
}

/*****************************************************************
 *  SECTION 2 - THE LOOK SWITCH (HR, clean or classic)             *
 *  What it does: remembers which of the three Customer Butler     *
 *  looks this person picked on this device, and tells every part  *
 *  of the page at once when the choice changes (the page itself   *
 *  AND App.js, which hides the suite bars for the HR look).       *
 *  Reads/Writes: localStorage "cb-skin-v2" (and reads the old     *
 *  "cb-skin" key once, so a classic choice is kept).              *
 *****************************************************************/

// WHY (2026-10-15, James): "what you made vs the design spec" - the design
// spec is HR Butler. The HR look is ADDED as a third skin and is the new
// default; the 2026-10-14 clean look and the classic look both stay, one tap
// away at the foot of every page (skins are only ever added, never removed).

const OLD_SKIN_STORAGE_KEY = 'cb-skin';    // the 2026-10-14 key: "classic" or "clean"
const SKIN_STORAGE_KEY = 'cb-skin-v2';     // the key from 2026-10-15 on
const SKIN_CHANGED_EVENT = 'cb-skin-changed'; // told to every listener on a switch

export const SKIN_HR = 'hr';             // the HR Butler look (new default)
export const SKIN_CLEAN = 'clean';       // the 2026-10-14 clean look
export const SKIN_CLASSIC = 'classic';   // the original customer pages

const KNOWN_SKINS = [SKIN_HR, SKIN_CLEAN, SKIN_CLASSIC]; // every look that exists

/* ---- readStoredSkin() -- the saved choice, or the HR look ----------
   In: nothing. Out: 'hr' | 'clean' | 'classic'.
   A person who picked classic before 2026-10-15 stays on classic.
   Storage can be blocked (private window); then the HR look is used. */
export function readStoredSkin() {
  try {
    const storedSkin = window.localStorage.getItem(SKIN_STORAGE_KEY);

    if (KNOWN_SKINS.includes(storedSkin)) {
      return storedSkin;
    }

    const oldStoredSkin = window.localStorage.getItem(OLD_SKIN_STORAGE_KEY);

    if (oldStoredSkin === SKIN_CLASSIC) {
      return SKIN_CLASSIC;
    }
  } catch (storageProblem) {
    // storage blocked: fall through to the default
  }

  return SKIN_HR;
}

/* ---- storeSkin(skinName) -- remembers the choice if it can ---- */
function storeSkin(skinName) {
  try {
    window.localStorage.setItem(SKIN_STORAGE_KEY, skinName);
  } catch (storageProblem) {
    // storage blocked: the choice lasts until the page reloads
  }
}

/* ---- tellEveryoneTheSkinChanged(skinName) -----------------------
   Fires one window event so App.js and the page switch together. */
function tellEveryoneTheSkinChanged(skinName) {
  const changeEvent = new CustomEvent(SKIN_CHANGED_EVENT, { detail: skinName });

  window.dispatchEvent(changeEvent);
}

/* ---- useCustomerSkin() -----------------------------------------
   Out: [skinName, chooseSkin]. chooseSkin('hr' | 'clean' |
   'classic') switches at once, remembers it, and tells App.js.  */
export function useCustomerSkin() {
  const [skinName, setSkinName] = useState(readStoredSkin);

  useEffect(() => {
    function followTheChange(changeEvent) {
      setSkinName(changeEvent.detail);
    }

    window.addEventListener(SKIN_CHANGED_EVENT, followTheChange);

    return () => window.removeEventListener(SKIN_CHANGED_EVENT, followTheChange);
  }, []);

  const chooseSkin = useCallback((nextSkin) => {
    const safeSkin = KNOWN_SKINS.includes(nextSkin) ? nextSkin : SKIN_HR;

    storeSkin(safeSkin);
    setSkinName(safeSkin);
    tellEveryoneTheSkinChanged(safeSkin);
  }, []);

  return [skinName, chooseSkin];
}

/*****************************************************************
 *  SECTION 3 - NO SUITE BARS ON THE HR LOOK                       *
 *  What it does: answers App.js's one question - "is this a       *
 *  Customer Butler page in the HR look?" - so App.js leaves out   *
 *  the suite bar, the alpha banner and the second module bar,     *
 *  exactly as HR Butler's page has none of them.                  *
 *  Reads: the address and the look choice.  Writes: nothing.      *
 *****************************************************************/

// WHY (2026-10-15, James's screenshots): three header rows (suite bar,
// "Customer Butler | Customers | ..." bar, "< Customers" breadcrumb) plus the
// yellow alpha banner sat above the content. HR Butler has ONE slim row.

// The addresses (inside /app) that belong to Customer Butler.
const CUSTOMER_BUTLER_ADDRESSES = [
  /^\/customers(\/[^/]+)?\/?$/,
  /^\/properties(\/[^/]+)?\/?$/,
  /^\/crm-setup\/?$/,
];

/* ---- isCustomerButlerAddress(pathname) ---------------------------
   In: the router's pathname ("/customers/123").
   Out: true when the page is one of Customer Butler's.          */
export function isCustomerButlerAddress(pathname) {
  const safePath = String(pathname || '');

  return CUSTOMER_BUTLER_ADDRESSES.some((onePattern) => onePattern.test(safePath));
}

/* ---- shouldHideSuiteShell(pathname, skinName) -------------------
   In: the pathname and the chosen look.
   Out: true only for a Customer Butler page in the HR look.
   Never hides the bars on any other page of the suite.          */
export function shouldHideSuiteShell(pathname, skinName) {
  const isHrLook = skinName === SKIN_HR;
  const isCustomerPage = isCustomerButlerAddress(pathname);

  return isHrLook && isCustomerPage;
}

/* ---- useHideSuiteShell(pathname) -- the hook App.js calls ---- */
export function useHideSuiteShell(pathname) {
  const [skinName] = useCustomerSkin();

  return shouldHideSuiteShell(pathname, skinName);
}
